// internationalization
import { useTranslation } from 'react-i18next';

// tanstack
import { Link } from '@tanstack/react-router';

// material ui icons
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import ArrowDropDownRoundedIcon from '@mui/icons-material/ArrowDropDownRounded';
import ArrowDropUpRoundedIcon from '@mui/icons-material/ArrowDropUpRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import RouterRoundedIcon from '@mui/icons-material/RouterRounded';

// material ui
import {
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  IconButton,
  LinearProgress,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

// shared components
import HtmlTooltip from '@/components/ui/tooltip/HtmlTooltip';

// feature-utils
import {
  getEnergySavings,
  getJobSubscriptionDataFiltered,
  getMaxLatencyConsumptionHours,
  getMaxLatencyHours,
  getPreviousEnergySavings,
  getQualityOfService,
} from '@/features/building-optimization/utils/utilityFunctions';

const OperationsDetailCard = ({ building, accounts, activeFilter }) => {
  const { t } = useTranslation('translation');
  const theme = useTheme();

  const energySavings = getEnergySavings(building);
  const energySavingsPrevious = getPreviousEnergySavings(building);
  const subscriptions = getJobSubscriptionDataFiltered(
    building,
    'PredictiveHeatControl',
  );

  // Assuming 'subscriptions' is already fetched outside of the map
  const gatewayHealthStatuses = subscriptions
    .filter((subscription) => {
      // Conditionally filter only active subscriptions if needed
      if (activeFilter) {
        return subscription?.StateCode === 'Active';
      }
      return true; // If activeFilter is false, include all subscriptions
    })
    .map((subscription) => {
      let uptimePercentage = 0; // Default to 0 if no valid data is found

      // Check and round the uptime percentage
      if (
        subscription?.State?.GatewayHealthCheck?.Data?.UptimePercentage !==
        undefined
      ) {
        uptimePercentage = Math.round(
          subscription.State.GatewayHealthCheck.Data.UptimePercentage,
        );
      }

      // Log a warning if the uptime is 0 or missing
      if (uptimePercentage === 0) {
        console.warn(
          `Uptime is 0 or missing for subscription: ${
            subscription?.DisplayName || 'Unknown'
          }`,
        );
      }

      // Calculate quality of service (if needed)
      const qos = getQualityOfService(subscription);

      // Return the mapped status object
      return {
        subscriptionId: subscription?.JobSubscriptionId,
        displayName: subscription?.DisplayName || 'Unknown',
        gatewayId: subscription?.UserInput?.GatewayId,
        uptimePercentage,
        qos,
      };
    })
    .filter((status) => status !== null); // Ensure the final result is filtered

  // Sort the gatewayHealthStatuses by uptimePercentage (ascending order)
  const sortedGatewayHealthStatuses = gatewayHealthStatuses.sort(
    (a, b) => a.uptimePercentage - b.uptimePercentage,
  );

  const minQoS = Math.min(
    ...(gatewayHealthStatuses?.map((status) => status.qos) ?? []),
  );

  const getProgressColor = (value) => {
    if (value >= 90)
      return theme.palette.mode === 'light' ? 'success.light' : 'success.dark';
    if (value >= 40)
      return theme.palette.mode === 'light' ? 'warning.light' : 'warning.dark';
    return theme.palette.mode === 'light' ? 'error.light' : 'error.dark';
  };

  const getLatencyColor = (hours) => {
    if (hours === null) {
      return theme.palette.mode === 'light'
        ? theme.palette.text.primary
        : theme.palette.text.secondary;
    }
    if (hours < 168) return theme.palette.success.main;
    if (hours < 336) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            {building.Name}
          </Typography>
          <Tooltip title={t('common:tooltips.viewBuilding')} placement="top">
            <IconButton
              sx={{ borderRadius: 1 }}
              component={Link}
              to={`/$accountId/operations/predictive-heat-control/buildings/${building.Name}`}
              state={{
                building,
                accounts,
              }}
            >
              <LaunchRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Divider sx={{ mb: 1, mt: 1 }} />

        <Stack>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">
                {t('translation:savings')}
              </Typography>
              <HtmlTooltip
                title={t('buildingOptimization:tooltips.savingsPercentage')}
                content={
                  <ListItemText
                    primary={
                      <Chip
                        icon={<ApartmentRoundedIcon />}
                        size="small"
                        sx={{ borderRadius: 1, mr: 1 }}
                        label={building.Name}
                      />
                    }
                    secondary={
                      typeof energySavings === 'number'
                        ? `${energySavings.toFixed(2)}%`
                        : 'N/A'
                    }
                    slotProps={{
                      secondary: {
                        color:
                          energySavings < 7.5
                            ? 'error.main'
                            : energySavings <= 18
                              ? 'warning.main'
                              : 'success.main',
                      }
                    }}
                  />
                }
              >
                <InfoRoundedIcon
                  fontSize="small"
                  sx={{ ml: 0.5, color: 'divider' }}
                />
              </HtmlTooltip>
            </Box>

            <Box>
              {energySavingsPrevious.length > 0 ? (
                <ListItemText
                  primary={energySavingsPrevious[0]?.label}
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {/* {energySavingsPrevious[0]?.value < energySavings 
                          ? <ArrowDropUpRoundedIcon sx={{ color: theme.palette.success.main }} /> 
                          : <ArrowDropDownRoundedIcon sx={{ color: theme.palette.error.main }} />
                      } */}
                      {energySavingsPrevious[0]?.value.toFixed(1) + '% '}
                    </Box>
                  }
                  slotProps={{
                    primary: {
                      fontSize: '0.8rem',
                      color: theme.palette.text.secondary,
                    },

                    secondary: {
                      component: 'span',
                      color: theme.palette.text.primary,
                    }
                  }} />
              ) : (
                <ListItemText
                  primary="N/A"
                  secondary="No data found for previous season"
                  slotProps={{
                    primary: {
                      fontSize: '0.8rem',
                      color: theme.palette.text.secondary,
                    },

                    secondary: {
                      component: 'span',
                      color: theme.palette.text.primary,
                    }
                  }} />
              )}
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'end' }}>
              {energySavings ? (
                <ListItemText
                  primary="Indeværende"
                  secondary={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {energySavingsPrevious[0]?.value < energySavings ? (
                        <ArrowDropUpRoundedIcon
                          sx={{ color: theme.palette.success.main }}
                        />
                      ) : (
                        <ArrowDropDownRoundedIcon
                          sx={{ color: theme.palette.error.main }}
                        />
                      )}
                      {energySavings.toFixed(1) + '%'}
                    </Box>
                  }
                  slotProps={{
                    primary: {
                      fontSize: '0.8rem',
                      color: theme.palette.text.secondary,
                    },

                    secondary: {
                      component: 'span',
                      color: theme.palette.text.primary,
                    }
                  }} />
              ) : (
                <ListItemText
                  primary="N/A"
                  secondary="No data"
                  slotProps={{
                    primary: {
                      fontSize: '0.8rem',
                      color: theme.palette.text.secondary,
                    },

                    secondary: {
                      component: 'span',
                      color: theme.palette.text.primary,
                    }
                  }} />
              )}
            </Box>
          </Box>

          <Divider sx={{ mt: 1, mb: 1 }} />

          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">
                {' '}
                {t('translation:energy-data-upload')}
              </Typography>
              <HtmlTooltip
                title={t('buildingOptimization:tooltips.billingMeterLatency')}
                content={
                  <ListItemText
                    primary={
                      <Chip
                        icon={<ApartmentRoundedIcon />}
                        size="small"
                        sx={{ borderRadius: 1, mr: 1 }}
                        label={building.Name}
                      />
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{
                          color: getLatencyColor(getMaxLatencyHours(building)),
                          mt: 1,
                        }}
                      >
                        {getMaxLatencyHours(building)?.toFixed(2)}{' '}
                        {t('units.hour.indefinitePlural', { ns: 'time' })}
                      </Typography>
                    }
                  />
                }
              >
                {getMaxLatencyHours(building) ? (
                  <InfoRoundedIcon
                    fontSize="small"
                    sx={{ ml: 0.5, color: 'divider' }}
                  />
                ) : (
                  <ErrorRoundedIcon
                    fontSize="small"
                    color="error"
                    sx={{ ml: 0.5 }}
                  />
                )}
              </HtmlTooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'end' }}>
              <Typography variant="body1" style={{ cursor: 'pointer' }}>
                {getMaxLatencyHours(building) ? (
                  <Typography
                    component="span"
                    sx={{
                      color: getLatencyColor(getMaxLatencyHours(building)),
                    }}
                  >
                    {Math.round(getMaxLatencyHours(building))}{' '}
                    {t('units.hour.indefinitePlural', { ns: 'time' })}
                  </Typography>
                ) : (
                  <Typography
                    component="span"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    N/A
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">
                {' '}
                {t('operations.higherConsumptionHours', {
                  ns: 'buildingOptimization',
                })}
              </Typography>
              <HtmlTooltip
                title={t('operations.higherConsumptionHours', {
                  ns: 'buildingOptimization',
                })}
                content={
                  <ListItemText
                    primary={
                      <Chip
                        icon={<ApartmentRoundedIcon />}
                        size="small"
                        sx={{ borderRadius: 1, mr: 1 }}
                        label={building.Name}
                      />
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        sx={{
                          color: getLatencyColor(
                            getMaxLatencyConsumptionHours(building),
                          ),
                          mt: 1,
                        }}
                      >
                        {getMaxLatencyConsumptionHours(building)?.toFixed(2)}{' '}
                        {t('units.hour.indefinitePlural', { ns: 'time' })}
                      </Typography>
                    }
                  />
                }
              >
                {getMaxLatencyConsumptionHours(building) ? (
                  <InfoRoundedIcon
                    fontSize="small"
                    sx={{ ml: 0.5, color: 'divider' }}
                  />
                ) : (
                  <ErrorRoundedIcon
                    fontSize="small"
                    color="error"
                    sx={{ ml: 0.5 }}
                  />
                )}
              </HtmlTooltip>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'end' }}>
              <Typography variant="body1" style={{ cursor: 'pointer' }}>
                {getMaxLatencyConsumptionHours(building) ? (
                  <Typography
                    component="span"
                    sx={{
                      color: getLatencyColor(
                        getMaxLatencyConsumptionHours(building),
                      ),
                    }}
                  >
                    {Math.round(getMaxLatencyConsumptionHours(building))}{' '}
                    {t('units.hour.indefinitePlural', { ns: 'time' })}
                  </Typography>
                ) : (
                  <Typography
                    component="span"
                    sx={{ color: theme.palette.text.secondary }}
                  >
                    N/A
                  </Typography>
                )}
              </Typography>
            </Box>
          </Box>

          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="body2">QoS</Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'end' }}>
              <Typography component="span">{minQoS}</Typography>
            </Box>
          </Box>

          {gatewayHealthStatuses.length > 0 && (
            <Box
              sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography variant="body2">
                  {t('translation:uptime')}
                </Typography>
                <HtmlTooltip
                  title={t('buildingOptimization:tooltips.gatewayUptimes')}
                  content={sortedGatewayHealthStatuses.map((status, index) => (
                    <ListItemText
                      key={index}
                      primary={
                        <div style={{ padding: '5px' }}>
                          <Chip
                            icon={<RouterRoundedIcon />}
                            size="small"
                            sx={{ borderRadius: 1, mr: 1 }}
                            label={status.gatewayId}
                          />
                          {status.displayName}{' '}
                        </div>
                      }
                      secondary={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <LinearProgress
                            variant="determinate"
                            value={status.uptimePercentage}
                            sx={{
                              flex: 1,
                              height: 10,
                              borderRadius: 5,
                              mr: 1,
                              '& .MuiLinearProgress-bar': {
                                backgroundColor: getProgressColor(
                                  status.uptimePercentage,
                                ),
                              },
                            }}
                          />
                          <Typography fontSize="0.8rem" color="text.secondary">
                            {status.uptimePercentage.toFixed(0)}%
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                >
                  <InfoRoundedIcon
                    fontSize="small"
                    sx={{ ml: 0.5, color: 'divider' }}
                  />
                </HtmlTooltip>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'end' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    mb: 1,
                    cursor: 'pointer',
                    minWidth: 150,
                  }}
                >
                  <LinearProgress
                    variant="determinate"
                    value={Math.min(
                      ...gatewayHealthStatuses.map(
                        (status) => status.uptimePercentage,
                      ),
                    )}
                    sx={{
                      flex: 1,
                      height: 10,
                      borderRadius: 5,
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getProgressColor(
                          Math.min(
                            ...gatewayHealthStatuses.map(
                              (status) => status.uptimePercentage,
                            ),
                          ),
                        ),
                      },
                    }}
                  />
                  <Typography
                    fontSize="0.8rem"
                    color="text.secondary"
                    sx={{ ml: 1 }}
                  >
                    {Math.min(
                      ...gatewayHealthStatuses.map(
                        (status) => status.uptimePercentage,
                      ),
                    )}
                    %
                  </Typography>
                </Box>
              </Box>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default OperationsDetailCard;
